import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  ArrowLeft,
  Settings,
  Clock,
  Calendar as CalendarIcon,
  Wrench,
  Plus,
  Trash2,
  Loader2,
  Save,
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday", short: "Mon" },
  { value: 1, label: "Tuesday", short: "Tue" },
  { value: 2, label: "Wednesday", short: "Wed" },
  { value: 3, label: "Thursday", short: "Thu" },
  { value: 4, label: "Friday", short: "Fri" },
  { value: 5, label: "Saturday", short: "Sat" },
  { value: 6, label: "Sunday", short: "Sun" },
];

const SERVICE_CATEGORIES = [
  { value: "mobile", label: "Mobile" },
  { value: "laptop", label: "Laptop" },
  { value: "tablet", label: "Tablet" },
  { value: "desktop", label: "Desktop" },
  { value: "printer", label: "Printer" },
  { value: "general", label: "General" },
];

export default function AppointmentSettings() {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  // Settings state
  const [settings, setSettings] = useState({
    is_booking_enabled: false,
    slot_duration_minutes: 60,
    advance_booking_hours: 2,
    max_advance_days: 30,
    booking_instructions: "",
    confirmation_message: "Your appointment has been confirmed. We look forward to seeing you!",
  });

  // Services state
  const [services, setServices] = useState([]);
  const [predefinedServices, setPredefinedServices] = useState({});
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: "",
    description: "",
    category: "general",
    estimated_time: "",
    base_price: "",
    is_active: true,
  });

  // Time slots state
  const [slots, setSlots] = useState([]);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showBulkSlotModal, setShowBulkSlotModal] = useState(false);
  const [slotForm, setSlotForm] = useState({
    day_of_week: 0,
    start_time: "09:00",
    end_time: "10:00",
    max_bookings: 3,
  });
  const [bulkSlotForm, setBulkSlotForm] = useState({
    days: [0, 1, 2, 3, 4, 5],
    start_time: "09:00",
    end_time: "18:00",
    slot_duration: 60,
    max_bookings: 3,
  });

  // Blocked dates state
  const [blockedDates, setBlockedDates] = useState([]);
  const [showBlockedDateModal, setShowBlockedDateModal] = useState(false);
  const [selectedBlockDate, setSelectedBlockDate] = useState(null);
  const [blockReason, setBlockReason] = useState("");

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [settingsRes, servicesRes, slotsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/appointments/settings`),
        axios.get(`${API}/appointments/services`),
        axios.get(`${API}/appointments/slots`),
        axios.get(`${API}/appointments/blocked-dates`),
      ]);

      setSettings(settingsRes.data);
      setServices(servicesRes.data.services || []);
      setPredefinedServices(servicesRes.data.predefined || {});
      setSlots(slotsRes.data.slots || []);
      setBlockedDates(blockedRes.data.blocked_dates || []);
    } catch (error) {
      console.error("Failed to fetch appointment settings:", error);
      toast.error("Failed to load appointment settings");
    } finally {
      setLoading(false);
    }
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/appointments/settings`, settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Service handlers
  const handleInitDefaultServices = async () => {
    try {
      await axios.post(`${API}/appointments/services/init-defaults`);
      toast.success("Default services initialized");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initialize services");
    }
  };

  const handleSaveService = async () => {
    if (!serviceForm.name.trim()) {
      toast.error("Service name is required");
      return;
    }

    try {
      const payload = {
        ...serviceForm,
        estimated_time: serviceForm.estimated_time ? parseInt(serviceForm.estimated_time) : null,
        base_price: serviceForm.base_price ? parseFloat(serviceForm.base_price) : null,
      };

      if (editingService) {
        await axios.put(`${API}/appointments/services/${editingService.id}`, payload);
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/appointments/services`, payload);
        toast.success("Service created");
      }

      setShowServiceModal(false);
      setEditingService(null);
      setServiceForm({
        name: "",
        description: "",
        category: "general",
        estimated_time: "",
        base_price: "",
        is_active: true,
      });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save service");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;

    try {
      await axios.delete(`${API}/appointments/services/${serviceId}`);
      toast.success("Service deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const openEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      description: service.description || "",
      category: service.category || "general",
      estimated_time: service.estimated_time ? String(service.estimated_time) : "",
      base_price: service.base_price ? String(service.base_price) : "",
      is_active: service.is_active !== false,
    });
    setShowServiceModal(true);
  };

  // Time slot handlers
  const handleSaveSlot = async () => {
    try {
      await axios.post(`${API}/appointments/slots`, slotForm);
      toast.success("Time slot created");
      setShowSlotModal(false);
      setSlotForm({
        day_of_week: 0,
        start_time: "09:00",
        end_time: "10:00",
        max_bookings: 3,
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create slot");
    }
  };

  const handleBulkCreateSlots = async () => {
    if (bulkSlotForm.days.length === 0) {
      toast.error("Please select at least one day");
      return;
    }

    try {
      const response = await axios.post(`${API}/appointments/slots/bulk`, null, {
        params: {
          days: bulkSlotForm.days,
          start_time: bulkSlotForm.start_time,
          end_time: bulkSlotForm.end_time,
          slot_duration: bulkSlotForm.slot_duration,
          max_bookings: bulkSlotForm.max_bookings,
        },
        paramsSerializer: { indexes: null },
      });
      toast.success(response.data.message);
      setShowBulkSlotModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create slots");
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/appointments/slots/${slotId}`);
      toast.success("Slot deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete slot");
    }
  };

  const handleDeleteAllSlots = async () => {
    if (!window.confirm("Are you sure you want to delete ALL time slots?")) return;

    try {
      await axios.delete(`${API}/appointments/slots`);
      toast.success("All slots deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete slots");
    }
  };

  const toggleSlotActive = async (slot) => {
    try {
      await axios.put(`${API}/appointments/slots/${slot.id}`, {
        is_active: !slot.is_active,
      });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update slot");
    }
  };

  // Blocked dates handlers
  const handleBlockDate = async () => {
    if (!selectedBlockDate) {
      toast.error("Please select a date");
      return;
    }

    const dateStr = selectedBlockDate.toISOString().split("T")[0];

    try {
      await axios.post(`${API}/appointments/blocked-dates`, {
        date: dateStr,
        reason: blockReason,
      });
      toast.success("Date blocked");
      setShowBlockedDateModal(false);
      setSelectedBlockDate(null);
      setBlockReason("");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to block date");
    }
  };

  const handleUnblockDate = async (blockedId) => {
    try {
      await axios.delete(`${API}/appointments/blocked-dates/${blockedId}`);
      toast.success("Date unblocked");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to unblock date");
    }
  };

  // Group slots by day
  const slotsByDay = DAYS_OF_WEEK.map((day) => ({
    ...day,
    slots: slots.filter((s) => s.day_of_week === day.value),
  }));

  const bookingUrl = `https://${tenant?.subdomain}.aftersales.pro/book`;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6 animate-in" data-testid="appointment-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Appointment Settings</h1>
              <p className="text-muted-foreground">
                Configure online booking for your customers
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={settings.is_booking_enabled ? "default" : "secondary"}>
              {settings.is_booking_enabled ? "Booking Enabled" : "Booking Disabled"}
            </Badge>
          </div>
        </div>

        {/* Booking URL Preview */}
        {settings.is_booking_enabled && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-green-800">Your Booking Page</p>
                  <p className="text-sm text-green-600 font-mono">{bookingUrl}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(bookingUrl);
                      toast.success("Link copied!");
                    }}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(bookingUrl, "_blank")}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Services
            </TabsTrigger>
            <TabsTrigger value="slots" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Time Slots
            </TabsTrigger>
            <TabsTrigger value="blocked" className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Blocked Dates
            </TabsTrigger>
          </TabsList>

          {/* General Settings Tab */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Configure basic appointment booking options
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Booking */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <p className="font-medium">Enable Online Booking</p>
                    <p className="text-sm text-muted-foreground">
                      Allow customers to book appointments through your booking page
                    </p>
                  </div>
                  <Switch
                    checked={settings.is_booking_enabled}
                    onCheckedChange={(checked) =>
                      setSettings({ ...settings, is_booking_enabled: checked })
                    }
                    data-testid="enable-booking-switch"
                  />
                </div>

                <Separator />

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Slot Duration */}
                  <div className="space-y-2">
                    <Label>Slot Duration</Label>
                    <Select
                      value={String(settings.slot_duration_minutes)}
                      onValueChange={(v) =>
                        setSettings({ ...settings, slot_duration_minutes: parseInt(v) })
                      }
                    >
                      <SelectTrigger data-testid="slot-duration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="90">1.5 hours</SelectItem>
                        <SelectItem value="120">2 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Advance Booking Hours */}
                  <div className="space-y-2">
                    <Label>Minimum Advance Booking</Label>
                    <Select
                      value={String(settings.advance_booking_hours)}
                      onValueChange={(v) =>
                        setSettings({ ...settings, advance_booking_hours: parseInt(v) })
                      }
                    >
                      <SelectTrigger data-testid="advance-hours-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 hour before</SelectItem>
                        <SelectItem value="2">2 hours before</SelectItem>
                        <SelectItem value="4">4 hours before</SelectItem>
                        <SelectItem value="24">1 day before</SelectItem>
                        <SelectItem value="48">2 days before</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Max Advance Days */}
                  <div className="space-y-2">
                    <Label>Maximum Days in Advance</Label>
                    <Select
                      value={String(settings.max_advance_days)}
                      onValueChange={(v) =>
                        setSettings({ ...settings, max_advance_days: parseInt(v) })
                      }
                    >
                      <SelectTrigger data-testid="max-days-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* Booking Instructions */}
                <div className="space-y-2">
                  <Label>Booking Instructions</Label>
                  <Textarea
                    value={settings.booking_instructions}
                    onChange={(e) =>
                      setSettings({ ...settings, booking_instructions: e.target.value })
                    }
                    placeholder="Any special instructions for customers (e.g., bring device charger, ID proof required)"
                    rows={3}
                    data-testid="booking-instructions-input"
                  />
                </div>

                {/* Confirmation Message */}
                <div className="space-y-2">
                  <Label>Confirmation Message</Label>
                  <Textarea
                    value={settings.confirmation_message}
                    onChange={(e) =>
                      setSettings({ ...settings, confirmation_message: e.target.value })
                    }
                    placeholder="Message shown after booking is confirmed"
                    rows={2}
                    data-testid="confirmation-message-input"
                  />
                </div>

                <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Services Offered</CardTitle>
                    <CardDescription>
                      Services customers can select when booking
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {services.length === 0 && (
                      <Button
                        variant="outline"
                        onClick={handleInitDefaultServices}
                        data-testid="init-defaults-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Load Default Services
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        setEditingService(null);
                        setServiceForm({
                          name: "",
                          description: "",
                          category: "general",
                          estimated_time: "",
                          base_price: "",
                          is_active: true,
                        });
                        setShowServiceModal(true);
                      }}
                      data-testid="add-service-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Service
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {services.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No services configured</p>
                    <p className="text-sm">Add services or load defaults to get started</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {SERVICE_CATEGORIES.map((cat) => {
                      const catServices = services.filter((s) => s.category === cat.value);
                      if (catServices.length === 0) return null;
                      return (
                        <div key={cat.value} className="space-y-2">
                          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                            {cat.label}
                          </h4>
                          <div className="grid gap-2">
                            {catServices.map((service) => (
                              <div
                                key={service.id}
                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                  !service.is_active ? "opacity-50 bg-muted" : ""
                                }`}
                              >
                                <div>
                                  <p className="font-medium">{service.name}</p>
                                  {service.description && (
                                    <p className="text-sm text-muted-foreground">
                                      {service.description}
                                    </p>
                                  )}
                                  <div className="flex gap-2 mt-1">
                                    {service.estimated_time && (
                                      <Badge variant="outline">~{service.estimated_time} min</Badge>
                                    )}
                                    {service.base_price && (
                                      <Badge variant="outline">From ₹{service.base_price}</Badge>
                                    )}
                                    {!service.is_active && (
                                      <Badge variant="secondary">Inactive</Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditService(service)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteService(service.id)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <Separator className="my-4" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time Slots Tab */}
          <TabsContent value="slots">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Time Slots</CardTitle>
                    <CardDescription>
                      Define available appointment slots for each day
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {slots.length > 0 && (
                      <Button
                        variant="outline"
                        onClick={handleDeleteAllSlots}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear All
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => setShowBulkSlotModal(true)}
                      data-testid="bulk-slots-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Bulk Create
                    </Button>
                    <Button
                      onClick={() => setShowSlotModal(true)}
                      data-testid="add-slot-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Slot
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {slots.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No time slots configured</p>
                    <p className="text-sm">Use "Bulk Create" to quickly set up your schedule</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {slotsByDay.map((day) => (
                      <div key={day.value} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium w-24">{day.label}</h4>
                          {day.slots.length === 0 && (
                            <Badge variant="secondary">Closed</Badge>
                          )}
                        </div>
                        {day.slots.length > 0 && (
                          <div className="flex flex-wrap gap-2 ml-24">
                            {day.slots
                              .sort((a, b) => a.start_time.localeCompare(b.start_time))
                              .map((slot) => (
                                <div
                                  key={slot.id}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                                    slot.is_active
                                      ? "bg-primary/5 border-primary/20"
                                      : "bg-muted opacity-50"
                                  }`}
                                >
                                  <span>
                                    {slot.start_time} - {slot.end_time}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    max {slot.max_bookings}
                                  </Badge>
                                  <button
                                    onClick={() => toggleSlotActive(slot)}
                                    className="text-muted-foreground hover:text-foreground"
                                    title={slot.is_active ? "Disable" : "Enable"}
                                  >
                                    {slot.is_active ? (
                                      <Check className="w-3 h-3 text-green-600" />
                                    ) : (
                                      <AlertCircle className="w-3 h-3" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSlot(slot.id)}
                                    className="text-muted-foreground hover:text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blocked Dates Tab */}
          <TabsContent value="blocked">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Blocked Dates</CardTitle>
                    <CardDescription>
                      Block specific dates (holidays, closures, etc.)
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setShowBlockedDateModal(true)}
                    data-testid="block-date-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Block Date
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {blockedDates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No blocked dates</p>
                    <p className="text-sm">Add holidays or closure dates</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {blockedDates.map((blocked) => (
                      <div
                        key={blocked.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-red-50 border-red-200"
                      >
                        <div>
                          <p className="font-medium">
                            {new Date(blocked.date).toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                          {blocked.reason && (
                            <p className="text-sm text-muted-foreground">{blocked.reason}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnblockDate(blocked.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Service Modal */}
        <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingService ? "Edit Service" : "Add Service"}</DialogTitle>
              <DialogDescription>
                Configure service details for customer booking
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  placeholder="e.g., Screen Repair"
                  data-testid="service-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  placeholder="Brief description of the service"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={serviceForm.category}
                    onValueChange={(v) => setServiceForm({ ...serviceForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estimated Time (min)</Label>
                  <Input
                    type="number"
                    value={serviceForm.estimated_time}
                    onChange={(e) =>
                      setServiceForm({ ...serviceForm, estimated_time: e.target.value })
                    }
                    placeholder="60"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Base Price (optional)</Label>
                  <Input
                    type="number"
                    value={serviceForm.base_price}
                    onChange={(e) =>
                      setServiceForm({ ...serviceForm, base_price: e.target.value })
                    }
                    placeholder="500"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={serviceForm.is_active}
                    onCheckedChange={(checked) =>
                      setServiceForm({ ...serviceForm, is_active: checked })
                    }
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowServiceModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveService} data-testid="save-service-btn">
                {editingService ? "Update" : "Create"} Service
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Single Slot Modal */}
        <Dialog open={showSlotModal} onOpenChange={setShowSlotModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Time Slot</DialogTitle>
              <DialogDescription>Create a single appointment slot</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select
                  value={String(slotForm.day_of_week)}
                  onValueChange={(v) =>
                    setSlotForm({ ...slotForm, day_of_week: parseInt(v) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={String(day.value)}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={slotForm.start_time}
                    onChange={(e) =>
                      setSlotForm({ ...slotForm, start_time: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={slotForm.end_time}
                    onChange={(e) =>
                      setSlotForm({ ...slotForm, end_time: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Max Bookings per Slot</Label>
                <Input
                  type="number"
                  min="1"
                  value={slotForm.max_bookings}
                  onChange={(e) =>
                    setSlotForm({ ...slotForm, max_bookings: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSlotModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSlot} data-testid="save-slot-btn">
                Create Slot
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Slot Modal */}
        <Dialog open={showBulkSlotModal} onOpenChange={setShowBulkSlotModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Bulk Create Slots</DialogTitle>
              <DialogDescription>
                Create multiple time slots at once
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Days</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day.value}
                      onClick={() => {
                        const days = bulkSlotForm.days.includes(day.value)
                          ? bulkSlotForm.days.filter((d) => d !== day.value)
                          : [...bulkSlotForm.days, day.value];
                        setBulkSlotForm({ ...bulkSlotForm, days });
                      }}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        bulkSlotForm.days.includes(day.value)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background hover:bg-accent"
                      }`}
                    >
                      {day.short}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Hours Start</Label>
                  <Input
                    type="time"
                    value={bulkSlotForm.start_time}
                    onChange={(e) =>
                      setBulkSlotForm({ ...bulkSlotForm, start_time: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Business Hours End</Label>
                  <Input
                    type="time"
                    value={bulkSlotForm.end_time}
                    onChange={(e) =>
                      setBulkSlotForm({ ...bulkSlotForm, end_time: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Slot Duration</Label>
                  <Select
                    value={String(bulkSlotForm.slot_duration)}
                    onValueChange={(v) =>
                      setBulkSlotForm({ ...bulkSlotForm, slot_duration: parseInt(v) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Max Bookings</Label>
                  <Input
                    type="number"
                    min="1"
                    value={bulkSlotForm.max_bookings}
                    onChange={(e) =>
                      setBulkSlotForm({
                        ...bulkSlotForm,
                        max_bookings: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkSlotModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkCreateSlots} data-testid="bulk-create-btn">
                Create Slots
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Block Date Modal */}
        <Dialog open={showBlockedDateModal} onOpenChange={setShowBlockedDateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Block Date</DialogTitle>
              <DialogDescription>
                Select a date to block from bookings
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Calendar
                mode="single"
                selected={selectedBlockDate}
                onSelect={setSelectedBlockDate}
                disabled={(date) => date < new Date()}
                className="rounded-md border mx-auto"
              />
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Input
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="e.g., Christmas Holiday"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBlockedDateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleBlockDate}
                disabled={!selectedBlockDate}
                data-testid="confirm-block-btn"
              >
                Block Date
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
